import Card from 'components/Card';
import CardContent from 'components/CardContent';
import styles from './PaymentDetail.module.scss';
import ButtonWithIcon from 'components/ButtonWithIcon';
import {BellIcon} from 'pages/DeliveryPlanner/components/OrderDetailsModal/icons';
import {CreditCardIcon} from 'pages/PaymentForm/icons';
import classNames from 'classnames';
import {OrderType, Prescription} from 'types';
import {BanknoteIcon} from '../icons/banknote';
import {useMemo} from 'react';
import {useCompanyConfig} from 'hooks/useCompanyConfig';
import {formatPrice} from 'utils/format-price';

interface Props {
  drugs: Prescription[];
  status?: string;
  orderType?: OrderType;
  onCharge: (id: number | undefined) => void;
  onNotifyToPay: () => void;
  onChargeManually: () => void;
}

function PaymentDetail({
  drugs,
  status,
  orderType,
  onCharge,
  onNotifyToPay,
  onChargeManually,
}: Props) {
  const {labels} = useCompanyConfig();
  const paymentDetail = useMemo(() => {
    const obj = {
      total_amount: 0,
      total_paid: 0,
      amount_due: 0,
    };
    drugs?.forEach((curr: Prescription) => {
      obj.total_amount += Number(curr.total_amount || 0);
      obj.total_paid += Number(curr.paid || 0);
      obj.amount_due += Number(curr.amount_due || 0);
    });
    return obj;
  }, [drugs]);

  const isDisabled = useMemo(() => {
    return (
      drugs?.filter((item) => Number(item.amount_due) > 0).length === 0 ||
      (status === 'completed'
        ? drugs?.filter((item) => item.status !== 'completed').length === 0
        : false)
    );
  }, [drugs, status]);

  const isChargeDisabled = useMemo(() => {
    return (
      drugs?.filter((item) => Number(item.amount_due) > 0).length === 0 ||
      status === 'completed'
    );
  }, [drugs, status]);

  return (
    <>
      <Card className={styles.card}>
        <CardContent pt="md" pb="sm">
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>{labels.payment}</h2>
          </div>
          <div className={styles.paymentGrid}>
            <div className={styles.paymentItem}>
              <div className={styles.paymentLabel}>Total amount</div>
              <div
                className={classNames(styles.paymentValue, styles.totalAmount)}
              >
                {formatPrice(paymentDetail.total_amount)}
              </div>
            </div>

            <div className={styles.paymentItem}>
              <div className={styles.paymentLabel}>Total paid</div>
              <div
                className={classNames(styles.paymentValue, styles.totalPaid)}
              >
                {formatPrice(paymentDetail.total_paid)}
              </div>
            </div>

            <div className={styles.paymentItem}>
              <div className={styles.paymentLabel}>Amount due</div>
              <div
                className={classNames(styles.paymentValue, styles.amountDue)}
              >
                {formatPrice(paymentDetail.amount_due)}
              </div>
            </div>
          </div>
          {orderType !== 'pickup' && (
            <div className={styles.row}>
              <ButtonWithIcon
                size="lg"
                disabled={isDisabled}
                color="ghost"
                onClick={onNotifyToPay}
                className={styles.notify_action}
                icon={<BellIcon width={16} height={16} />}
              >
                Notify to Pay
              </ButtonWithIcon>
              <ButtonWithIcon
                size="lg"
                disabled={isDisabled}
                color="ghost"
                onClick={onChargeManually}
                className={styles.notify_action}
                icon={<BanknoteIcon width={16} height={16} />}
              >
                Manual Charge
              </ButtonWithIcon>
              <ButtonWithIcon
                size="lg"
                color="ghost"
                onClick={() => onCharge(undefined)}
                disabled={isChargeDisabled}
                className={classNames(styles.notify_action)}
                icon={<CreditCardIcon width={16} height={16} />}
              >
                Charge Card
              </ButtonWithIcon>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}
export default PaymentDetail;
